import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import logo from "../../assets/logo.png";

export const SUPPORT_EMAIL = "support@medicorepay.com";

interface LegalPageLayoutProps {
  title: string;
  description: string;
  children: ReactNode;
}

interface LegalSectionProps {
  title: string;
  children: ReactNode;
}

export function LegalPageLayout({
  title,
  description,
  children,
}: LegalPageLayoutProps) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | MediCorePay`;

    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <a
        href="#legal-content"
        className="sr-only fixed left-4 top-4 z-50 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white focus:not-sr-only"
      >
        Skip to content
      </a>

      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <Link
            to="/login"
            className="flex w-fit items-center gap-3 rounded-lg text-slate-900"
            aria-label="MediCorePay sign in"
          >
            <img src={logo} alt="" className="h-11 w-11 object-contain" />
            <span className="text-xl font-black tracking-tight">
              <span className="text-blue-700">Medi</span>
              <span className="text-red-700">Core</span>
              <span className="text-teal-700">Pay</span>
            </span>
          </Link>

          <nav
            aria-label="Legal and account links"
            className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-slate-600"
          >
            <Link className="hover:text-blue-700" to="/privacy">
              Privacy
            </Link>
            <Link className="hover:text-blue-700" to="/terms">
              Terms
            </Link>
            <Link className="hover:text-blue-700" to="/account-deletion">
              Account deletion
            </Link>
            <Link className="hover:text-blue-700" to="/login">
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main
        id="legal-content"
        className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14"
      >
        <Link
          to="/login"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
        >
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          Back to sign in
        </Link>

        <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-br from-blue-700 via-blue-700 to-teal-600 px-6 py-10 text-white sm:px-10">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.16em] text-blue-100">
              Effective and last updated 1 August 2026
            </p>
            <h1 className="text-3xl font-black tracking-tight sm:text-5xl">
              {title}
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-blue-50 sm:text-lg">
              {description}
            </p>
          </div>

          <div className="space-y-10 px-6 py-9 text-[15px] leading-7 text-slate-700 sm:px-10 sm:py-12 sm:text-base">
            {children}
          </div>
        </article>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-7 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <p>© 2026 MediCorePay. All rights reserved.</p>
          <a
            className="inline-flex w-fit items-center gap-2 font-semibold text-blue-700 hover:text-blue-900"
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            <Mail aria-hidden="true" className="h-4 w-4" />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </footer>
    </div>
  );
}

export function LegalSection({ title, children }: LegalSectionProps) {
  return (
    <section>
      <h2 className="mb-3 text-xl font-black tracking-tight text-slate-950 sm:text-2xl">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
